import api from '@/lib/api/axios'
import type { Object as ObjectEntity, ObjectVersion } from '@/types/entities'
import type { ListParams, PaginatedResponse } from '@/lib/api/types'

export interface RecoveryChainTier {
    tier: string | null
    chainSet: string | null
    analytics: number | null
    size: number | null
    metadata: string | null
}

/**
 * Egress disk-image conversion.
 *
 * The wire carries TARGET FORMAT CODES, never platform names — 'vhdx', not 'Hyper-V'. The mapping
 * from platform to format is resolved once in the UI, because it is the part most easily got
 * wrong: Azure rejects both vhdx and vhd-dynamic and accepts only vhd-fixed. Everything
 * downstream — this API, the egress Lambdas, VSCopyFile — speaks codes.
 *
 * An object absent from the map recovers in raw, the format the vault already holds.
 */
export type ConvertFormats = Record<string, string[]>

/**
 * Per-object choice between the compromised copy and the last clean one.
 * 'red' = the compromised copy, 'green' = the last clean one. An object with no entry follows the
 * request-wide recoverRedObjects answer, so omitting this changes nothing.
 *
 * Unlike ConvertFormats this is a STORED PROCEDURE PARAMETER, not something stamped afterwards:
 * it decides which Recovery_Chain the request is built from, so it cannot be changed once the
 * request exists.
 */
export type RedObjectChoices = Record<string, 'red' | 'green'>

export interface ConversionOption {
    objectId: string
    classification: string | null
    category: string | null
    /**
     * 'raw' | 'vhdx' | 'ova' | 'qcow2' | 'vmdk-flat' | 'vmdk-sparse' | 'vmdk-streamoptimized' |
     * 'vmdk-unspecified' | 'vhd-fixed' | 'vhd-dynamic' | 'vhd-unspecified' | 'unknown'.
     * Never guesses raw.
     */
    sourceFormat: string
    /**
     * Where sourceFormat came from. 'metadata' = the agent declared it in metadata.json.
     * 'filelist' = we inferred it from the saved file names, which cannot see a VMDK's subformat.
     * 'none' = neither could answer.
     */
    formatSource: 'metadata' | 'filelist' | 'none'
    /** Only what VSCopyFile can actually produce, read from ConversionMatrix. */
    allowedTargets: string[]
    canConvert: boolean
    hasDiskImage: boolean
    /**
     * The saved file list was cut off by its varchar(1000) ceiling, so "no disk image" here means
     * "could not tell", not "there isn't one". Only ever true when formatSource is not 'metadata'.
     */
    fileListTruncated: boolean
}

export interface ConversionOptionsResponse {
    mode: string
    scenario: number
    objects: ConversionOption[]
    convertibleCount: number
    totalCount: number
    hasIncompleteEvidence: boolean
}

/** What the three recovery-request endpoints return. */
export interface RecoveryRequestResult {
    success: boolean
    sqlState?: string
    sqlMessage?: string
    outcome?: string
    /** Minted inside CreateRecoverRequests and now returned, so the caller can stamp its choices. */
    requestId?: string | null
    /**
     * Objects the request could NOT include. Previously these vanished in silence while the proc
     * still reported success.
     */
    excluded?: Array<{
        objectId: string
        /** 'clean-copy-requested-none-exists' | 'primary-compromised-no-clean-copy' */
        reason: string
    }>
    conversion?: {
        requested: number
        accepted: number
        rowsStamped: number
        /** Targets refused rather than silently dropped, so the UI can say why. */
        rejected: Array<{
            objectId: string
            requested: string[]
            allowed: string[]
            reason: string
            unavailable?: string[]
            sourceFormat?: string
        }>
    } | null
}

export const objectsApi = {
    list: (params?: ListParams & { limit?: number }) => {
        // Convert perPage to limit for backend
        const backendParams = params ? {
            ...params,
            limit: params.limit || params.perPage || 20,
            page: params.page || 1,
        } : { page: 1, limit: 20 }
        // Remove perPage if it exists since backend uses limit
        if (backendParams.perPage) {
            delete backendParams.perPage
        }
        return api.get<PaginatedResponse<ObjectEntity>>('/api/objects', { params: backendParams })
    },

    get: (id: string, scenario?: string) =>
        api.get<ObjectEntity>(`/api/objects/${id}`, { params: scenario ? { scenario } : {} }),

    create: (data: Omit<ObjectEntity, 'objectID'>) =>
        api.post<ObjectEntity>('/api/objects', data),

    update: (id: string, data: Partial<Omit<ObjectEntity, 'objectID'>>) =>
        api.put<ObjectEntity>(`/api/objects/${id}`, data),

    delete: (id: string) =>
        api.delete(`/api/objects/${id}`),

    getVersions: (id: string) =>
        api.get<ObjectVersion[]>(`/api/objects/${id}/versions`),

    getRecoveryChain: (id: string) =>
        api.get<{ success: boolean; data: RecoveryChainTier[] }>(`/api/objects/${encodeURIComponent(id)}/recovery-chain`),

    // `layerID` here is a legacy WIRE name only: the API fills it from
    // RolledUpRTORPOSubObjects.SourceLayer, which is Classifications.DisplayRank
    // (the RPO sibling below already calls the same value sourceLayer). Renaming
    // it is a backend change — doing it here alone would just read undefined.
    getRolledUpRTO: (id: string, scenario?: string) =>
        api.get<Array<{ objectID: string; iconFilename: string | null; objectDescription: string; layerID: number | null; rto: number | null }>>(`/api/objects/${id}/rolled-up-rto`, { params: scenario ? { scenario } : {} }),

    getRolledUpRPO: (id: string, scenario?: string) =>
        api.get<Array<{ objectID: string; iconFilename: string | null; objectDescription: string; sourceLayer: number | null; rpo: number | null }>>(`/api/objects/${id}/rolled-up-rpo`, { params: scenario ? { scenario } : {} }),

    getRtoDetails: (id: string, scenario?: string) =>
        api.get<{ egressTime: number | null; linkTime: number | null; engineerRTO: number | null; endToEndTime: number | null }>(`/api/objects/${id}/rto-details`, { params: scenario ? { scenario } : {} }),

    getServiceTable: (id: string, scenario?: string) =>
        api.get<Array<{
            sourceID: string;
            description: string;
            sourceLayer: number | null;
            calculatedRPO: number | null;
            endToEndTime: number | null;
            metadata: string | null;
            vaultFiles: string | null;
            objectStatus: number | null;
            objectSize: number | null;
        }>>(`/api/objects/${id}/service-table`, { params: scenario ? { scenario } : {} }),

    getRunbook: (id: string, scenario?: string) =>
        api.get<Array<import('@/types/entities').RunbookTask>>(`/api/objects/${id}/runbook`, { params: scenario ? { scenario } : {} }),

    // Method recommendation for this object's runbook — computed once per display.
    getMethodSuggestion: (id: string) =>
        api.get<{ success: boolean; data: { suggestion: import('./utils/runbookAutomation').MethodSuggestion | null } }>(
            `/api/objects/${encodeURIComponent(id)}/method-suggestion`),

    saveRunbook: (id: string, tasks: Array<import('@/types/entities').RunbookTask>, scenario?: string) =>
        api.post<{ success: boolean; taskCount: number }>(`/api/objects/${id}/runbook`, { tasks, scenario }),

    changeRunbookFromMaster: (id: string, masterRunbookId: number, scenario: string) =>
        api.post<{ success: boolean; taskCount: number }>(`/api/objects/${id}/runbook/change-from-master`, { masterRunbookId, scenario }),

    cloneRunbook: (id: string, sourceObjectID: string, sourceScenario: string, scenario: string) =>
        api.post<{ success: boolean; taskCount: number }>(`/api/objects/${id}/runbook/clone`, { sourceObjectID, sourceScenario, scenario }),

    saveRunbookAsMaster: (id: string, scenario: string, name: string, description: string) =>
        api.post<{ success: boolean; masterID: number; taskCount: number }>(`/api/objects/${id}/runbook/save-as-master`, { scenario, name, description }),

    getScenarios: (id: string) =>
        api.get<Array<{ id: number; name: string; isDefault: boolean }>>(`/api/objects/${id}/scenarios`),

    getAllScenarios: () =>
        api.get<Array<{ id: number; name: string; description: string; useCase: string; isDefault: boolean }>>('/api/objects/scenarios/all'),

    getScenario: (id: string, scenarioId: number) =>
        api.get<{ id: number; name: string; description: string; useCase: string }>(`/api/objects/${id}/scenarios/${scenarioId}`),

    getObjectsInformation: (id: string, scenario?: string) =>
        api.get<{ id: number | null; scenario: number | null; objectID: string | null; floorPlan: number | null; documentID: number | null; videoID: number | null; image: string | null }>(`/api/objects/${id}/information`, { params: scenario ? { scenario } : {} }),

    // Dropdown data endpoints
    checkObjectIDUnique: (objectID: string) =>
        api.get<{ isUnique: boolean }>(`/api/objects/check-unique/${encodeURIComponent(objectID)}`),

    getTeams: () =>
        api.get<Array<{ id: number; teamName: string }>>('/api/objects/teams'),

    getTechnologies: () =>
        api.get<Array<{ id: number; name: string; category: string }>>('/api/objects/technologies'),

    getBackupSolutions: () =>
        api.get<Array<{ id: number; name: string }>>('/api/objects/backup-solutions'),

    getMasterRunbooks: () =>
        api.get<Array<{ id: number; description: string }>>('/api/objects/master-runbooks'),

    getMasterRunbookInfo: (runbookId: number) =>
        api.get<{ engineerRTO: number | null; numberOfTasks: number | null; tasks: Array<any> }>(`/api/objects/master-runbooks/${runbookId}`),

    getStatsCount: () =>
        api.get<{ objects: number; services: number }>('/api/objects/stats/count'),

    getStatsByLocation: () =>
        api.get<{ objects: Array<{ Location: string; NoObjects: number }>; services: Array<{ Location: string; NoObjects: number }> }>('/api/objects/stats/by-location'),

    retrieveVersion: (data: { objectID: string; versionID: number | null; recoverRedObjects: string; convertFormats?: ConvertFormats; scanFirst?: boolean }) =>
        api.post<RecoveryRequestResult>(`/api/objects/${data.objectID}/versions/${data.versionID}/retrieve`, {
            scanFirst: data.scanFirst,
            recoverRedObjects: data.recoverRedObjects,
            convertFormats: data.convertFormats,
        }),

    retrieveObject: (data: { objectID: string; recoverRedObjects: string; scenario?: number; convertFormats?: ConvertFormats; redObjectChoices?: RedObjectChoices; scanFirst?: boolean }) =>
        api.post<RecoveryRequestResult>(`/api/objects/${data.objectID}/retrieve`, {
            scanFirst: data.scanFirst,
            recoverRedObjects: data.recoverRedObjects,
            scenario: data.scenario,
            convertFormats: data.convertFormats,
            redObjectChoices: data.redObjectChoices,
        }),

    /**
     * Which objects in a pending recovery can be converted on egress, and to what.
     * Asked BEFORE the request exists, so the server recomputes the scope the same way
     * CreateRecoverRequests will.
     */
    getConversionOptions: (
        id: string,
        params: { mode: 'stack' | 'service' | 'object' | 'version'; scenario?: number; minSourceLayer?: number },
    ) =>
        api.get<ConversionOptionsResponse>(`/api/objects/${encodeURIComponent(id)}/conversion-options`, {
            params,
        }),

    // `maxLayerID` is a legacy WIRE name only: the API computes it from the
    // object's Classifications.DisplayRank, on the same scale as minSourceLayer.
    // Renaming it is a backend change.
    getStackRecoveryStats: (id: string, minSourceLayer?: number) => {
        const params = minSourceLayer !== undefined ? `?minSourceLayer=${minSourceLayer}` : '';
        return api.get<{ servicesCount: number; objectsCount: number; redObjectsCount: number; minSourceLayer: number; maxLayerID: number }>(`/api/objects/${id}/stack-recovery-stats${params}`);
    },

    getRedObjects: (id: string) =>
        api.get<Array<{
            redId: number | null;
            objectID: string;
            objectSecondary: string;
            redObjectName: string;
            redAnalytics: number | null;
            redDateTime: string | null;
            redSize: number | null;
            greenId: number | null;
            greenObjectName: string;
            greenAnalytics: number | null;
            greenDateTime: string | null;
            greenSize: number | null;
        }>>(`/api/objects/${id}/red-objects`),

    requestStackRecovery: (data: { objectID: string; recoverRedObjects: string; minSourceLayer: number; scenario: number; convertFormats?: ConvertFormats; redObjectChoices?: RedObjectChoices; scanFirst?: boolean }) =>
        api.post<RecoveryRequestResult>(`/api/objects/${data.objectID}/stack-recovery-request`, {
            recoverRedObjects: data.recoverRedObjects,
            minSourceLayer: data.minSourceLayer,
            scenario: data.scenario,
            scanFirst: data.scanFirst,
            convertFormats: data.convertFormats,
            redObjectChoices: data.redObjectChoices,
        }),
    getAnomalySizeChart: (id: string) =>
        api.get<AnomalySizePoint[]>(`/api/objects/${id}/anomaly-size-chart`),

    getAnomalyHistory: (objectID: string) =>
        api.get<AnomalyHistoryPoint[]>(`/api/objects/${encodeURIComponent(objectID)}/anomaly-history`),

    importObjects: (file: File, objectService: string = 'Object') => {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('objectService', objectService)
        return api.post<ImportResult>('/api/objects/import', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
    },

    importMapping: (file: File, scenarioId: number) => {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('scenarioId', String(scenarioId))
        return api.post<ImportResult>('/api/objects/import-mapping', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
    },

    updateMarker: (objectID: string, floorPlanX: number | null, floorPlanY: number | null) =>
        api.patch<{ success: boolean; data: { objectID: string; floorPlanX: number | null; floorPlanY: number | null } }>(`/api/objects/${encodeURIComponent(objectID)}/marker`, { floorPlanX, floorPlanY }),

    // Documents
    getDocuments: (id: string, scenario?: string) =>
        api.get<ObjectDocument[]>(`/api/objects/${id}/documents`, { params: scenario ? { scenario } : {} }),

    addDocument: (id: string, data: { scenario: number; filename: string; originalName: string; description?: string; fileSize?: number; mimeType?: string }) =>
        api.post(`/api/objects/${id}/documents`, data),

    deleteDocument: (id: string, docId: number) =>
        api.delete(`/api/objects/${id}/documents/${docId}`),

    getDocumentLibrary: () =>
        api.get<ObjectDocument[]>('/api/objects/documents/library'),

    // Videos
    getVideos: (id: string, scenario?: string) =>
        api.get<ObjectVideo[]>(`/api/objects/${id}/videos`, { params: scenario ? { scenario } : {} }),

    addVideo: (id: string, data: { scenario: number; filename: string; originalName: string; description?: string; fileSize?: number; mimeType?: string; duration?: number }) =>
        api.post(`/api/objects/${id}/videos`, data),

    deleteVideo: (id: string, videoId: number) =>
        api.delete(`/api/objects/${id}/videos/${videoId}`),

    getVideoLibrary: () =>
        api.get<ObjectVideo[]>('/api/objects/videos/library'),
}

export interface ObjectDocument {
    id: number
    objectID?: string
    scenario?: number
    filename: string
    originalName: string
    description?: string | null
    fileSize?: number | null
    mimeType?: string | null
    uploadedAt?: string
    url: string
}

export interface ObjectVideo {
    id: number
    objectID?: string
    scenario?: number
    filename: string
    originalName: string
    description?: string | null
    fileSize?: number | null
    mimeType?: string | null
    duration?: number | null
    uploadedAt?: string
    url: string
    downloadUrl?: string
}

export interface AnomalyHistoryPoint {
    id: number
    objectID: string
    objectName: string
    datetime: string | null
    sizeMB: number | null
    noVaultFiles: number | null
    anomalyScore: number
    anomalyData: any
    objectSecondary: string
    transferTime: number | null
    analytics: number | null
    analyticsText?: string | null
}

export interface ImportResult {
    totalInFile: number
    imported: number
    alreadyExisting: number
    errors: Array<{ row: number; objectID?: string; error: string }>
}

export interface AnomalySizePoint {
    id: number | null
    objectName: string
    datetime: string
    size: number | null
    sizeMB: number | null
    anomaly: string | null
    backupType: string
    meanSize: number | null
    stdSize: number | null
    lowerBound: number | null
    upperBound: number | null
}
